// Inventory (คลังของ) — the item master and its statistics.
//
// The stock ledger answers "what moved?"; this answers "what IS there, and how
// fast does it go?". Both read the same `stock` table — the difference is that
// everything here comes from GET /stock/stat, which aggregates server-side.

import {
  TYPE_CUT, rows,
  INVENTORY_MAIN_CATEGORIES, INVENTORY_SUB_CATEGORIES, INVENTORY_UNITS,
} from './stock.js'

/* ── GET /stock/stat ──────────────────────────────────────────────────────── */
//
// `stat_type` is a MODE SELECTOR, not a filter (same shape of trap as
// /inventory's `type`): "time" groups by month, "branch" groups by branch, and
// the stack has a conditional per mode with nothing in between.
export const STAT_TIME   = 'time'
export const STAT_BRANCH = 'branch'

// `N` is `sum(Ncal)`, so it carries the ledger's sign: เบิกใช้ rows sum
// NEGATIVE. Usage is shown as a magnitude — "ใช้ไป 40" not "ใช้ไป −40" — so
// every usage read goes through here rather than being flipped ad hoc.
export const usageMagnitude = (n) => Math.abs(Number(n) || 0)

/* ── branches ─────────────────────────────────────────────────────────────── */
//
// สนามเป้า (id 1) is the legacy/HQ branch: it is hidden from every booking
// dropdown in the app and is not a cut target (lib/stock.js#stockDefaultBranch
// maps it to อารีย์). It is excluded from the per-branch breakdown for the same
// reason — but NOT silently dropped: if it still holds a non-zero level, the
// detail page prints it as a footnote, so the branch bars and the clinic total
// always reconcile.
export const LEGACY_BRANCH_ID = 1

/**
 * Normalize GET /stock/stat?stat_type=branch → sorted rows the chart can take.
 * Xano returns [{ branch_id, N, branch }]; `branch` is resolved server-side per
 * row, so we only fall back to the branch list when it is missing.
 *
 * @returns {{ visible: Array, legacy: Object|null, total: number }}
 *   visible = branches to chart (สนามเป้า removed)
 *   legacy  = the สนามเป้า row when it holds a non-zero level, else null
 *   total   = clinic-wide level INCLUDING สนามเป้า (it is real stock)
 */
export function splitBranchStat(stat, branchNameById = {}) {
  const all = rows(stat).map((r) => ({
    branch_id: Number(r?.branch_id) || 0,
    value: Number(r?.N) || 0,
    label: r?.branch || branchNameById[Number(r?.branch_id)] || `สาขา ${r?.branch_id ?? '—'}`,
  }))
  const total  = all.reduce((s, r) => s + r.value, 0)
  const legacy = all.find((r) => r.branch_id === LEGACY_BRANCH_ID && r.value !== 0) || null
  const visible = all.filter((r) => r.branch_id !== LEGACY_BRANCH_ID)
  return { visible, legacy, total }
}

/* ── time range ───────────────────────────────────────────────────────────── */

export const RANGES = [
  { months: 1,  label: '1 เดือน' },
  { months: 3,  label: '3 เดือน' },
  { months: 6,  label: '6 เดือน' },
  { months: 12, label: '1 ปี' },
]
export const DEFAULT_RANGE_MONTHS = 6

const pad = (n) => String(n).padStart(2, '0')
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/**
 * The last `months` CALENDAR months, ending today.
 *
 * Whole months, not "N×30 days": the chart's buckets are calendar months, so a
 * day-based window would open on a half-empty leading bar that reads as a
 * collapse in usage rather than as a partial month.
 */
export function monthRange(months) {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1)
  return { start_date: iso(start), end_date: iso(now) }
}

/**
 * Fill the gaps in GET /stock/stat?stat_type=time.
 *
 * Xano returns ONLY months that had movement. Handing that straight to a chart
 * would draw a month of zero usage as if it never existed — the x-axis would
 * silently close up and two non-adjacent months would look adjacent. So we
 * generate every bucket in the window and merge the data onto it.
 *
 * @returns [{ year, month, value }] oldest → newest, `value` a magnitude.
 */
export function fillMonths(stat, months) {
  const byKey = new Map()
  for (const r of rows(stat)) {
    const y = Number(r?.year), m = Number(r?.month)
    if (!y || !m) continue
    byKey.set(`${y}-${m}`, usageMagnitude(r?.N))
  }
  const now = new Date()
  const out = []
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const y = d.getFullYear(), m = d.getMonth() + 1
    out.push({ year: y, month: m, value: byKey.get(`${y}-${m}`) || 0 })
  }
  return out
}

/** Average monthly usage over the window, ignoring the CURRENT (partial) month. */
export function avgPerMonth(points) {
  const past = points.slice(0, -1)
  if (!past.length) return 0
  return past.reduce((s, p) => s + p.value, 0) / past.length
}

/**
 * Months of cover left: current level ÷ average monthly usage.
 * `null` when there is no usage history to divide by — an item that has never
 * moved has no burn rate, and "∞ months of stock" would be a lie dressed as a
 * number.
 *
 * A level of 0 OR BELOW clamps to 0. A negative level is a bookkeeping error
 * (see negativeBranches below), and dividing it would print "−1.4 เดือน" —
 * a confidently precise number describing something that cannot exist.
 */
export function monthsOfCover(level, points) {
  const avg = avgPerMonth(points)
  if (!(avg > 0)) return null
  const lvl = Number(level) || 0
  if (lvl <= 0) return 0
  return lvl / avg
}

/* ── negative levels = the ledger is wrong ────────────────────────────────── */
//
// Physical stock cannot be less than nothing, so a negative branch level always
// means a MISSING ROW, not a shortage:
//   • a เบิกใช้ recorded with no matching รับเพิ่ม for the delivery,
//   • one half of a transfer written without its pair (รับย้าย/แบ่งให้ must
//     come in twos — see lib/stock.js), or
//   • a physical count that needed a ปรับ and never got one.
// The fix is to add the missing row, not to order more — which is why the UI
// colours this as an error (rose) and never as "low stock" (amber).

/** The branches whose level is below zero, worst first. `[]` when all is well. */
export function negativeBranches(branchRows) {
  return (branchRows || [])
    .filter((r) => (Number(r?.value) || 0) < 0)
    .sort((a, b) => a.value - b.value)
}

/* ── choice lists (หมวดหมู่ · หมวดย่อย · หน่วย) ──────────────────────────── */
//
// GET /inventory's OTHER three modes exist exactly for this: `mainCAT`,
// `subCAT` and `unit` return aggregate pickers shaped `[{choice}]`. Use them
// rather than deriving the lists from a page of items — a derived list only
// contains what happens to be on screen, so a category with no loaded items
// silently disappears from the picker and gets re-created as a duplicate.
export const CHOICE_MODES = {
  mainCategory: INVENTORY_MAIN_CATEGORIES,
  subCategory: INVENTORY_SUB_CATEGORIES,
  unit: INVENTORY_UNITS,
}

/** Normalize a `[{choice}]` aggregate (tolerating bare strings) → sorted strings. */
export function normalizeChoices(res) {
  const arr = rows(res)
  return [...new Set(
    arr.map((c) => (typeof c === 'string' ? c : (c?.choice ?? c?.value ?? c?.name ?? '')))
      .map((s) => String(s).trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'th'))
}

/**
 * Fetch one choice list.
 * @param get   an apiGet-shaped (path, params) => Promise
 * @param field 'mainCategory' | 'subCategory' | 'unit'
 */
export async function fetchChoices(get, field) {
  const mode = CHOICE_MODES[field]
  if (!mode) return []
  return normalizeChoices(await get('/api/inventory', { type: mode, per_page: 500 }))
}

/* ── the item record ──────────────────────────────────────────────────────── */

export const itemName = (it) => String(it?.Name || '').trim() || `#${it?.id ?? '—'}`

/**
 * Is this item below its reorder point? `min` is per the whole clinic (it is a
 * single column on the item, not per branch). 0 / null / absent = not tracked,
 * which is NOT the same as "min 0" — an untracked item must never render as
 * "ถึงจุดสั่งซื้อ" just because it hit zero.
 */
export function belowMin(level, item) {
  const min = Number(item?.min) || 0
  if (min <= 0) return false
  return (Number(level) || 0) <= min
}

/**
 * Best-effort current level straight off a GET /inventory?type=normal row.
 *
 * The list endpoint carries a `stock_count` addon, but its shape is not pinned
 * down in the docs — it has been seen as a bare number and as an object. The
 * LIST uses this to avoid one /stock/stat call per row; the DETAIL page never
 * does, because there we can afford the authoritative aggregate. Returns null
 * when the shape is unrecognised, and the caller shows "—" rather than 0: a
 * confident wrong zero reads as "we are out of it".
 */
export function listLevel(item) {
  if (!item || typeof item !== 'object') return null

  // Candidate carriers, most specific first. `stock_count` is the documented
  // addon name; the rest are the shapes GET /stock uses for the same idea
  // (`stock_N` bare, `stockall.stock_N` all-branches, `stock_dynamic.stock_N`),
  // which the inventory addon may well reuse.
  const carriers = [
    item.stock_count, item.stockCount, item.stock,
    item.stockall, item.stock_dynamic, item,
  ]
  for (const c of carriers) {
    const n = readLevel(c)
    if (n != null) return n
  }
  return null
}

// One carrier → a number, or null when the shape says nothing. Handles: a bare
// number; { stock_N | N | total | sum | count }; an array of rows to sum; and
// one level of nesting (e.g. { stockall: { stock_N } }).
function readLevel(v, depth = 0) {
  if (v == null) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  // A numeric STRING is a real answer; '' / 'abc' are not.
  if (typeof v === 'string') {
    const s = v.trim()
    return s !== '' && Number.isFinite(Number(s)) ? Number(s) : null
  }
  if (Array.isArray(v)) {
    if (!v.length) return null
    // Rows keyed by branch: sum whichever quantity field they carry.
    let sum = 0, hit = false
    for (const r of v) {
      const n = typeof r === 'number' ? r : readLevel(r?.N ?? r?.stock_N ?? r?.total ?? null, depth + 1)
      if (n != null) { sum += n; hit = true }
    }
    return hit ? sum : null
  }
  if (typeof v === 'object') {
    for (const k of ['stock_N', 'N', 'total', 'sum', 'count', 'value']) {
      const n = readLevel(v[k], depth + 1)
      if (n != null) return n
    }
    // One level deeper only — enough for { stockall: { stock_N } }, and bounded
    // so an unexpected shape cannot walk the whole object graph.
    if (depth < 1) {
      for (const k of Object.keys(v)) {
        if (!/stock/i.test(k)) continue
        const n = readLevel(v[k], depth + 1)
        if (n != null) return n
      }
    }
  }
  return null
}

/** Params for the usage chart's stat call. Usage means เบิกใช้ — nothing else. */
export function usageStatParams(inventoryId, months, branchId) {
  return {
    inventory_id: inventoryId,
    stat_type: STAT_TIME,
    type: TYPE_CUT,
    branch_id: branchId || undefined,
    ...monthRange(months),
  }
}
